import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AppShell } from '../app/AppShell';
import { isApiError } from '../api/client';
import {
  spaceDownloadUrl,
  useSpaceBlob,
  useSpaceFile,
  useSpaceHistory,
  useSpaceTree,
  useSpaces,
} from '../api/hooks';
import { IconBack, IconHistory } from '../components/icons';
import { Badge, Button, Card, EmptyState, ErrorText, Segmented, Spinner } from '../components/ui';
import { zh } from '../i18n';
import { formatDateTime, shortSha } from '../lib/format';
import { FileBrowser } from '../memory/FileBrowser';
import { useStreamState } from '../stream/StreamProvider';

/**
 * One space: 檔案 + 版本紀錄 + 下載 (SPEC §10, CONTRACTS §4.6).
 *
 * `403 SPACE_DENIED` is rendered as its own state rather than a generic error:
 * the gateway resolves the caller's grant on EVERY subroute here, so a denial
 * is a permission fact worth stating plainly, not a failure to retry.
 */
export function SpaceDetailRoute(): JSX.Element {
  const { slug = '' } = useParams<{ slug: string }>();
  const [tab, setTab] = useState<'files' | 'history'>('files');
  const [path, setPath] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [blobSha, setBlobSha] = useState<string | null>(null);

  const spaces = useSpaces();
  const space = spaces.data?.find((item) => item.slug === slug) ?? null;
  const tree = useSpaceTree(slug, path);
  const file = useSpaceFile(slug, blobSha === null ? selected : null);
  const history = useSpaceHistory(slug, selected ?? '', tab === 'history');
  const blob = useSpaceBlob(slug, blobSha, selected);

  // `space.committed` (CONTRACTS §5) means the sidecar's committer has landed a
  // new commit for this slug: what is on screen is now one revision behind.
  const commits = useStreamState().spaceCommits;
  const latestForSlug = commits.find((commit) => commit.slug === slug)?.sha ?? null;
  const refetchTree = tree.refetch;
  const refetchHistory = history.refetch;
  useEffect(() => {
    if (latestForSlug === null) return;
    void refetchTree();
    void refetchHistory();
  }, [latestForSlug, refetchTree, refetchHistory]);

  const denied =
    isApiError(tree.error, 'SPACE_DENIED') ||
    isApiError(history.error, 'SPACE_DENIED') ||
    isApiError(file.error, 'SPACE_DENIED');

  if (denied) {
    return (
      <AppShell title={space?.name ?? slug}>
        <EmptyState title={zh.spaces.denied} />
      </AppShell>
    );
  }

  return (
    <AppShell
      title={space?.name ?? slug}
      leading={
        <Link to="/spaces" className="btn btn--ghost btn--sm" aria-label={zh.app.back}>
          <IconBack size={18} />
        </Link>
      }
      trailing={
        space ? (
          <Badge tone={space.level === 'rw' ? 'success' : 'muted'}>
            {space.level === 'rw' ? zh.spaces.levelRw : zh.spaces.levelRo}
          </Badge>
        ) : null
      }
    >
      {space?.level === 'ro' ? (
        <div className="banner banner--info">
          <span className="banner__text">{zh.spaces.readOnlyNotice}</span>
        </div>
      ) : null}

      <Segmented
        options={[
          { value: 'files', label: zh.spaces.tabFiles },
          { value: 'history', label: zh.spaces.tabHistory },
        ]}
        value={tab}
        onChange={setTab}
        ariaLabel={zh.spaces.title}
      />

      <div style={{ marginTop: '0.75rem' }}>
        {tab === 'files' ? (
          blobSha !== null && selected !== null ? (
            <div className="stack">
              <div className="row">
                <Button variant="ghost" small onClick={() => setBlobSha(null)}>
                  {zh.spaces.backToCurrent}
                </Button>
                <span className="small">{zh.spaces.blobTitle(shortSha(blobSha))}</span>
              </div>
              {blob.isLoading ? <Spinner /> : null}
              <ErrorText error={blob.error} />
              {blob.data ? (
                <div className="file-view">
                  {blob.data.truncated ? (
                    <div className="banner banner--info">
                      <span className="banner__text">{zh.memory.truncated}</span>
                    </div>
                  ) : null}
                  <pre className="file-view__pre">{blob.data.content}</pre>
                </div>
              ) : null}
            </div>
          ) : (
            <div className="stack">
              <FileBrowser
                path={path}
                onPathChange={(next) => {
                  setSelected(null);
                  setBlobSha(null);
                  setPath(next);
                }}
                entries={tree.data?.entries}
                treeLoading={tree.isLoading}
                treeError={tree.error}
                selected={selected}
                onSelect={(next) => {
                  setBlobSha(null);
                  setSelected(next);
                }}
                file={file.data}
                fileLoading={file.isLoading}
                fileError={file.error}
                buildDownloadUrl={(target) => spaceDownloadUrl(slug, target)}
                rootLabel={space?.name ?? slug}
                emptyLabel={zh.memory.filesEmpty}
              />
              {selected !== null ? (
                <Button variant="ghost" small onClick={() => setTab('history')}>
                  <IconHistory size={16} /> {zh.spaces.tabHistory}
                </Button>
              ) : null}
            </div>
          )
        ) : null}

        {tab === 'history' ? (
          <div className="stack">
            {history.isLoading ? <Spinner /> : null}
            <ErrorText error={history.error} />
            {history.data && history.data.length === 0 ? (
              <EmptyState title={zh.spaces.historyEmpty} />
            ) : null}
            {history.data && history.data.length > 0 ? (
              <Card flush>
                <ul className="list">
                  {history.data.map((commit) => (
                    <li key={commit.sha}>
                      <div className="list__row" style={{ cursor: 'default' }}>
                        <div className="list__main">
                          <div className="list__title">{commit.message}</div>
                          <div className="list__sub">
                            {commit.authorLabel} · {formatDateTime(commit.committedAt)} ·{' '}
                            {zh.spaces.historyFiles(commit.files.length)}
                          </div>
                        </div>
                        {selected !== null ? (
                          <Button
                            variant="ghost"
                            small
                            onClick={() => {
                              setBlobSha(commit.sha);
                              setTab('files');
                            }}
                          >
                            {zh.spaces.viewAtCommit}
                          </Button>
                        ) : (
                          <span className="mono tiny faint">{shortSha(commit.sha)}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </Card>
            ) : null}
          </div>
        ) : null}
      </div>
    </AppShell>
  );
}
